import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  AttachFileOutlined,
  CloseOutlined,
  ExpandLessOutlined,
  ExpandMoreOutlined,
} from "@mui/icons-material";
import SendOutlined from "@mui/icons-material/SendOutlined";


import { IconButton } from "@mui/material";
import { isEmpty, size } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { useLoanTermsHook } from "../../hooks/useLoanTermsHook";
import { sidebarClear } from "../../redux/actions/sidebarActions";
import LoanEmailRecipientSelector from "../common/Loan/LoanEmailRecipientSelector";
import BlockNoteEditor from "../html/BlockNoteEditor";
import TextInput from "../inputs/TextInput";
import Flx from "../layout/Flx";
import SelectableLoanFilesTable from "../tables/SelectableLoanFilesTable";
import Txt from "../typography/Txt";

// ─── Constants ────────────────────────────────────────────────────────────────

const DIVIDER = "rgba(0,0,0,0.08)";

// ─── Main Component ───────────────────────────────────────────────────────────

const GenericBlockEmailEditor = ({
  template,
  allowAdvancedEditing = false,
  hideAttachments = false,
  allowCustomEmail = false,
  showToSelector = true,
  onSubmit,
}) => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const dispatch = useDispatch();
  const { sendEmailTemplate, loading } = useLoanTermsHook();
  const [to, setTo] = useState([]);
  const [cc, setCc] = useState([]);
  const [showCC, setShowCC] = useState(false);
  const [subject, setSubject] = useState(null);
  const [contentType, setContentType] = useState("html");
  const [emailBody, setEmailBody] = useState(null);
  const [attachments, setAttachments] = useState([]);

  // Ref to the BlockNote editor instance (for extracting HTML at send time)
  const editorRef = useRef(null);

  // Track the HTML string that BlockNote should render.
  // This is set from the template and used as the `value` prop.
  const [editorHtmlValue, setEditorHtmlValue] = useState(null);

  const onBlockNoteChange = useCallback((newValue) => {
    setEmailBody(newValue);
  }, []);

  const handleOnSuccess = () => {
    setTo([]);
    setCc([]);
    setShowCC(false);
    setSubject(null);
    setAttachments([]);
    setEmailBody(null);
    setEditorHtmlValue(null);
    setContentType("html");
    dispatch(sidebarClear());
  };

  const handleSend = useCallback(async () => {
    // Get the latest HTML from the BlockNote editor if available
    let bodyHtml = emailBody?.htmlString || emailBody;

    // If we have a ref to the editor, get the freshest content
    if (editorRef.current) {
      try {
        bodyHtml = await editorRef.current.blocksToHTMLLossy(
          editorRef.current.document,
        );
      } catch (err) {
        console.error("Failed to extract HTML from editor", err);
      }
    }

    const payload = {
      toRecipients: to,
      ccRecipients: cc,
      subject: subject,
      body: bodyHtml,
      attachmentIds: attachments?.map((a) => a._id) || [],
      contentType: contentType || "html",
    };

    if (onSubmit) {
      onSubmit(payload);
      return;
    }

    sendEmailTemplate({
      dealId: loanDrilldown?._id,
      templateName: template?.template_name,
      data: payload,
      onSuccessFn: () => handleOnSuccess(),
    });
  }, [
    to,
    cc,
    subject,
    emailBody,
    attachments,
    contentType,
    loanDrilldown?._id,
    template?.template_name,
    sendEmailTemplate,
    dispatch,
    onSubmit,
  ]);

  // Sync template into editor when it changes
  useEffect(() => {
    const html = template?.html_body || template?.body || template?.text || "";
    setEditorHtmlValue(html);
    setSubject(template?.subject || "");
  }, [template]);

  return (
    <Box>
      {/* To / Cc / Subject — structured field rows */}
      <Box
        sx={{
          mb: 1.5,
          border: `1px solid ${DIVIDER}`,
          borderRadius: "8px",
          overflow: "hidden",
          boxShadow: "0 1px 4px rgba(0,0,0,0.05)",
        }}
      >
        {showToSelector && (
          <HeaderInput label="To:" divider>
            <Flx ac sx={{ position: "relative" }}>
              <LoanEmailRecipientSelector
                value={to}
                onChange={setTo}
                allowCustomEmail={allowCustomEmail}
                sx={{ pr: "22px" }}
              />
              {!showCC && (
                <Button
                  variant="text"
                  sx={{
                    p: 0,
                    flexShrink: 0,
                    minWidth: 0,
                    textDecoration: "underline",
                    fontSize: "11px",
                    position: "absolute",
                    right: "12px",
                  }}
                  onClick={() => setShowCC(true)}
                >
                  Cc
                </Button>
              )}
            </Flx>
          </HeaderInput>
        )}
        {showCC && (
          <HeaderInput label="Cc:" divider>
            <Flx ac sx={{ position: "relative" }}>
              <LoanEmailRecipientSelector
                sx={{ pr: "22px" }}
                value={cc}
                allowCustomEmail={allowCustomEmail}
                onChange={setCc}
              />
              <IconButton
                size="small"
                sx={{
                  flexShrink: 0,
                  minWidth: 0,
                  textDecoration: "underline",
                  fontSize: "11px",
                  position: "absolute",
                  right: "5px",
                }}
                onClick={() => {
                  setCc([]);
                  setShowCC(false);
                }}
              >
                <CloseOutlined sx={{ fontSize: "16px !important" }} />
              </IconButton>
            </Flx>
          </HeaderInput>
        )}
        <HeaderInput label="Subject:">
          <TextInput
            value={subject}
            onChange={(val) => setSubject(val)}
            placeholder="Email subject..."
            sx={{ "& fieldset": { border: "none" } }}
          />
        </HeaderInput>
      </Box>

      {/* Body Editor — BlockNote */}
      <Box
        sx={{
          border: `1px solid ${DIVIDER}`,
          borderRadius: "8px",
          overflow: "hidden",
          boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
          ".bn-editor": {
            paddingInline: allowAdvancedEditing ? "54px" : "16px",
          },
        }}
      >
        <BlockNoteEditor
          value={editorHtmlValue}
          editorRef={editorRef}
          onChange={onBlockNoteChange}
          maxHeight="unset"
          minHeight="200px"
          backgroundColor="#ffffff"
          suppressOuterBorder
          borderRadius="0px"
          placeholder="Start typing here..."
          sideMenu={false}
        />
      </Box>

      {!hideAttachments && (
        <Box
          sx={{
            border: `1px solid ${DIVIDER}`,
            borderRadius: "8px",
            overflow: "hidden",
            mb: 2,
            // mt: 2,
            boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
          }}
        >
          <EmailAttachmentsSelector
            attachments={attachments}
            setAttachments={setAttachments}
          />
        </Box>
      )}

      {/* Footer: count + send */}
      <Flx ac g={1} end fw sx={{ mt: 2 }}>
        {!hideAttachments && (
          <Txt sx={{ fontSize: "12px", color: "#94a3b8" }}>
            {attachments.length > 0
              ? `${attachments.length} attachment${attachments.length !== 1 ? "s" : ""}`
              : "No attachments"}
          </Txt>
        )}
        <Button
          endIcon={<SendOutlined />}
          onClick={handleSend}
          disabled={isEmpty(to) || isEmpty(subject)}
          variant="contained"
          loading={loading}
          size="large"
        >
          Send Email
        </Button>
      </Flx>
    </Box>
  );
};

// ─── Recipient Selector ───────────────────────────────────────────────────────

// ─── Attachments Selector ─────────────────────────────────────────────────────

const EmailAttachmentsSelector = ({ attachments, setAttachments }) => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const loanDocuments = (loanDrilldown?.loanDocuments || []).filter(
    (d) => !d?.isHidden,
  );
  const [minimized, setMinimized] = useState(true);

  const attachmentOptions = useMemo(() => {
    return loanDocuments
      .map((a) => ({
        label: a?.file_display_name || "",
        value: a?._id,
        docType: a?.docType,
        uploadDate: a?.uploadDate,
        _id: a?._id,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [loanDocuments]);

  if (isEmpty(loanDocuments)) {
    return null;
  }
  return (
    <Box sx={{ mt: 1 }}>
      <Box
        onClick={() => setMinimized(!minimized)}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 1.5,
          cursor: "pointer",
          background: "#fafbfc",
          borderBottom:
            attachmentOptions.length > 0 ? `1px solid ${DIVIDER}` : "none",
          ":&:hover": { background: "#f8fafc" },
        }}
      >
        <Flx ac gap={1}>
          <AttachFileOutlined sx={{ fontSize: 14, color: "#94a3b8" }} />
          <Txt
            sx={{
              fontSize: "11px",
              fontWeight: 700,
              color: "#64748b",
              textTransform: "uppercase",
              letterSpacing: "0.07em",
            }}
          >
            Attached Documents ({size(attachments)})
          </Txt>
        </Flx>

        {!minimized ? (
          <ExpandLessOutlined sx={{ fontSize: 18 }} />
        ) : (
          <ExpandMoreOutlined sx={{ fontSize: 18 }} />
        )}
      </Box>

      {minimized ? null : (
        <SelectableLoanFilesTable
          height="400px"
          files={loanDocuments}
          selectedRows={attachments}
          setSelectedRows={setAttachments}
        />
      )}
    </Box>
  );
};

// ─── Header Input Row ─────────────────────────────────────────────────────────

const HeaderInput = ({ label, icon, g = 1, children, sx, divider }) => {
  return (
    <Flx
      ac
      gap={g}
      sx={{
        py: 0.25,
        background: "#ffffff",
        borderBottom: divider ? `1px solid ${DIVIDER}` : "none",
        "input, .MuiChip-label": {
          fontSize: "12px",
        },
        ...sx,
      }}
    >
      <Flx
        ac
        g={1}
        sx={{
          minWidth: 72,
          pl: 1.5,
          flexShrink: 0,
          borderRight: `1px solid ${DIVIDER}`,
          alignSelf: "stretch",
          display: "flex",
          alignItems: "center",
          background: "#fafbfc",
        }}
      >
        {icon}
        <Txt
          sx={{
            color: "#64748b",
            fontSize: "11px",
            fontWeight: 700,
            letterSpacing: "0.02em",
          }}
        >
          {label}
        </Txt>
      </Flx>
      <Box sx={{ flex: 1 }}>{children}</Box>
    </Flx>
  );
};

export default GenericBlockEmailEditor;
